import { GraphRequestError } from '../graph/graph-request-error';
import { OneDriveConsentError } from './onedrive-consent-error';

export type OneDriveSyncStep =
  | 'consent'
  | 'drive-folder'
  | 'library-ids'
  | 'shortcut-create'
  | 'shortcut-move';

export type OneDriveFailureDetail =
  | 'destination-missing'
  | 'incomplete-ids'
  | 'no-shortcut-id'
  | 'destination-name-taken'
  | 'root-name-taken'
  | 'root-item-vanished';

export class OneDriveStepError extends Error {
  constructor(
    public readonly step: OneDriveSyncStep,
    message: string,
    public readonly cause?: unknown,
    public readonly detail?: OneDriveFailureDetail,
  ) {
    super(message);
    this.name = 'OneDriveStepError';
  }
}

export type OneDriveFailureReason =
  | 'consent-blocked'
  | 'signed-out'
  | 'no-onedrive'
  | 'no-access'
  | 'name-taken'
  | 'folder-gone'
  | 'storage-full'
  | 'throttled'
  | 'network'
  | 'unknown';

export interface OneDriveFailure {
  reason: OneDriveFailureReason;
  code: string;
}

const CODE_PREFIX: Record<OneDriveSyncStep, string> = {
  consent: 'OD-AUTH',
  'drive-folder': 'OD-DIR',
  'library-ids': 'OD-LIB',
  'shortcut-create': 'OD-SC',
  'shortcut-move': 'OD-MV',
};

const STEPLESS_PREFIX = 'OD-UNK';

// A support code names the code path that raised the failure, not the user's situation.
const DETAIL_FAILURES: Record<OneDriveFailureDetail, OneDriveFailure> = {
  'destination-missing': { reason: 'folder-gone', code: 'OD-DIR-GONE' },
  'incomplete-ids': { reason: 'unknown', code: 'OD-LIB-IDS' },
  'no-shortcut-id': { reason: 'unknown', code: 'OD-SC-NOID' },
  'destination-name-taken': { reason: 'name-taken', code: 'OD-MV-TAKEN' },
  'root-name-taken': { reason: 'name-taken', code: 'OD-SC-TAKEN' },
  'root-item-vanished': { reason: 'unknown', code: 'OD-SC-GONE' },
};

const reasonForStatus = (
  step: OneDriveSyncStep | undefined,
  status: number,
): OneDriveFailureReason => {
  if (status === 401) return 'signed-out';
  if (status === 429 || status === 503) return 'throttled';
  if (status === 507) return 'storage-full';

  switch (step) {
    case 'drive-folder':
      if (status === 403) return 'no-onedrive';
      if (status === 404) return 'folder-gone';
      return 'unknown';
    case 'library-ids':
      return status === 403 || status === 404 ? 'no-access' : 'unknown';
    case 'shortcut-move':
      if (status === 409) return 'name-taken';
      if (status === 404) return 'folder-gone';
      return 'unknown';
    default:
      return 'unknown';
  }
};

export const classifyOneDriveFailure = (error: unknown): OneDriveFailure => {
  if (error instanceof OneDriveConsentError) {
    return { reason: 'consent-blocked', code: 'OD-AUTH-CONSENT' };
  }

  const tagged = error instanceof OneDriveStepError ? error : undefined;
  if (tagged?.detail) return DETAIL_FAILURES[tagged.detail];

  const prefix = tagged ? CODE_PREFIX[tagged.step] : STEPLESS_PREFIX;
  const inner = tagged ? (tagged.cause ?? tagged) : error;

  if (inner instanceof TypeError) {
    return { reason: 'network', code: `${prefix}-NET` };
  }

  if (inner instanceof GraphRequestError) {
    return {
      reason: reasonForStatus(tagged?.step, inner.status),
      code: `${prefix}-${inner.status}`,
    };
  }

  return {
    reason: 'unknown',
    code: tagged ? `${prefix}-ERR` : STEPLESS_PREFIX,
  };
};
